/* oxlint-disable react-doctor/rendering-hydration-mismatch-time */
import { buttonStyle, OFF_SECONDARY_CLASS } from 'components/buttons/buttonStyle';
import PermissionButton from 'components/buttons/PermissionButton.tsx';
import EmployeeName from 'components/employee/EmployeeName';
import { CircleAlert, WarningAmberOutlined } from 'components/icons';
import { useFormatDate } from 'components/misc/FormattedDate';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal.tsx';
import { useInvoiceSendValidation } from 'components/modals/invoices/hooks/useInvoiceSendValidation';
import { isSpecAttachedByDefault } from 'components/modals/invoices/specAttachment';
import { useModal } from 'components/modals/useModal.ts';
import StepDate from 'components/stepper/StepDate';
import { Alert, AlertTitle } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import Workflow, { InvoiceWorkflowStep } from 'components/workflow/Workflow';
import { format } from 'date-fns';
import useAuth from 'hooks/useAuth.ts';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { ReactNode, useCallback, useEffect, useEffectEvent, useId, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'store';
import { useMarkAsSentMutation } from 'store/api/emails.ts';
import { readApiErrorBody } from 'store/api/apiError';
import {
    APIInvoicesSlice,
    useResetKsefMutation,
    useSendToExternalApprovalMutation,
    useSendToInvoicingMutation,
    useSendToNetvisorMutation,
    useSkipKsefMutation,
} from 'store/api/invoices.ts';
import { useIsMaventaIntegrationEnabledQuery } from 'store/api/maventa-integration';
import { useIsNetvisorIntegrationEnabledQuery } from 'store/api/netvisor-integration';
import { FeatureFlag } from 'types/features.ts';
import {
    ExternalStatusType,
    InvoiceData,
    InvoiceExternalEventDto,
    InvoiceForm,
    InvoiceStatusType,
    InvoiceTransactionActionType,
    InvoiceType,
} from 'types/invoices';
import { MinDto, NamedEntityMinDto } from 'types/other';
import { errorAlert, warningAlert } from 'utils/alert';
import { getClassifierText, t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import { useInvoice } from './hooks/useInvoice';
import { useInvoiceFlow } from './hooks/useInvoiceFlow';
import { useInvoiceTransition } from './hooks/useInvoiceTransition';
import { ReasonTooltip } from './layout/components/ReasonTooltip';
import SendToIntegrationDialog, { NETVISOR_DIALOG_MESSAGE_IDS } from './layout/components/SendToIntegrationDialog';
import { formatShareRate } from './payers/paymentShare';
import { getInvoiceStageState, getInvoiceStageStatuses, InvoiceStageState } from './util/invoiceStages';
import { findLatestNonRejectedTransition, wasEverInStatus } from './util/invoiceStepTransition';
import {
    getAllResponsibleEmployees,
    getResponsibleEmployeesByState,
    getResponsibleOrSupervisingText,
    InvoiceTransitionAlertMessage,
} from './util/invoiceUtil';

interface InvoiceWorkflowProps {
    invoice: InvoiceData;
    handleStatusLoadingState: (isLoading: boolean) => void;
    isLoading: boolean;
    templateValidation: () => Promise<boolean>;
    onPostponeSuccess?: () => void;
    isInvoiceEmpty: boolean;
    isInvoiceContentLoaded: boolean;
    /** A card is open in edit: unsaved, as a changed form is, so the next step waits for its Save or Cancel. */
    hasOpenEdit?: boolean;
    /**
     * Opt-in, the new design: the stages and the buttons are handed to this function to place in the record's
     * band, instead of the classic vertical stepper with its buttons under it.
     */
    renderBand?: (band: InvoiceWorkflowBand) => ReactNode;
}

/** One of the workflow's buttons, as the new design's band draws it. */
export interface InvoiceWorkflowAction {
    key: string;
    labelId: string;
    /** `primary` is the one amber button, last; `plain` a text button; the rest are outlined. */
    look: 'primary' | 'outlined' | 'plain';
    onClick: () => void;
    disabled: boolean;
    /** Why the button waits: a line under the actions that describes it, drawn only while it is `disabled`. */
    reason?: string;
}

/** A stage of the invoice's progress, with what its caption says. */
export interface InvoiceWorkflowStage {
    status: InvoiceStatusType;
    label: string;
    state: InvoiceStageState;
    /** When the invoice entered the stage, and who moved it. */
    date?: string;
    by?: string;
    /** The integrations' events under Finalised: KSeF's, each with its reason under it, then Netvisor's and Maventa's sends. */
    details: string[];
}

export interface InvoiceWorkflowBand {
    stages: InvoiceWorkflowStage[];
    /** Who the next stage waits for: Draft's responsible persons, Reviewed's supervising partners. */
    pending?: { roleId: string; employees: NamedEntityMinDto[] };
    actions: InvoiceWorkflowAction[];
    /** The dialogs the buttons open and what a send left to read: mounted anywhere in the band. */
    extras: ReactNode;
}

export type HandleInvoiceTransitionArgs = {
    to: InvoiceStatusType;
    comment?: string;
    transactionActionType: InvoiceTransactionActionType;
};

const MAVENTA_DIALOG_MESSAGE_IDS = {
    title: 'invoices.maventa.confirm-dialog.title',
    message: 'invoices.maventa.confirm-dialog.message',
    attachSpec: 'invoices.maventa.confirm-dialog.attach-spec',
    specUnavailable: 'invoices.maventa.confirm-dialog.spec-unavailable',
    send: 'invoices.maventa.confirm-dialog.send',
};

const FOCUS_REFETCH_INTERVAL_MS = 30_000;

const SENT_TO_FINALIZED_PERMISSION = ['UPDATE.INVOICES.SENT_TO_FINALIZED'];

type StageStep = InvoiceWorkflowStep & { when?: { date: string; employee: MinDto | null } };

const PAYER_SPLITTING_STATUSES: string[] = [
    InvoiceStatusType.DRAFT,
    InvoiceStatusType.REVIEWED,
    InvoiceStatusType.APPROVED,
];

/** The payers a debit invoice is split between when it is finalised: one invoice each, a 0 % payer gets none. */
function payersSplitOnFinalise(invoice: InvoiceData, isSharedPaymentEnabled: boolean) {
    if (
        !isSharedPaymentEnabled ||
        invoice.invoiceTypeCode !== InvoiceType.DEBIT ||
        invoice.isSplit ||
        !PAYER_SPLITTING_STATUSES.includes(invoice.invoiceStatusTypeCode)
    ) {
        return [];
    }
    const payers = (invoice.payers ?? []).filter((payer) => payer.shareRate > 0);
    return payers.length > 1 ? payers : [];
}

const InvoiceWorkflow = (props: InvoiceWorkflowProps) => {
    const {
        invoice,
        handleStatusLoadingState,
        isLoading,
        templateValidation,
        onPostponeSuccess,
        isInvoiceEmpty,
        isInvoiceContentLoaded,
        hasOpenEdit,
        renderBand,
    } = props;

    const { invoiceTypeCode, invoiceStatusTypeCode, invoiceTransitions, paidOnDate } = invoice;
    const { buttonsDisabled } = useSelector((state) => state.buttons);
    const { user } = useAuth();
    const userId = user?.id;
    const { isDebit, isPrepayment, isCredit, isCorrection, isApproved, isPartiallyCredited } = useInvoice(invoice);
    const { openModal, closeModal } = useModal();
    const isJointInvoice = invoice.invoiceMatters && invoice.invoiceMatters.length > 1;
    const isMultiReviewApprovalEnabled = useIsFeatureEnabled(FeatureFlag.MULTI_REVIEW_APPROVE_JOINT_INVOICE);
    const isMultiApprovalFlow = isMultiReviewApprovalEnabled && isJointInvoice && isDebit;
    const isExternalApprovalEnabled = useIsFeatureEnabled(FeatureFlag.INVOICE_EXTERNAL_APPROVAL);
    const externalStatus = invoice.externalStatusTypeCode as ExternalStatusType | undefined;
    const isComplianceProhibitsInvoiceFinalizationEnabled = useIsFeatureEnabled(
        FeatureFlag.COMPLIANCE_PROHIBITS_INVOICE_FINALIZATION
    );
    const isSharedPaymentEnabled = useIsFeatureEnabled(FeatureFlag.INVOICE_SHARED_PAYMENT_FUNCTIONALITY);
    const isSharedPaymentCopy = isSharedPaymentEnabled && !!invoice.isSharedPayment;
    const sharedPaymentGroup = isSharedPaymentCopy ? invoice.sharedPaymentGroup : null;
    const isFinalizationDisabled =
        isComplianceProhibitsInvoiceFinalizationEnabled &&
        (isDebit || isPrepayment) &&
        invoice.invoiceStatusTypeCode === InvoiceStatusType.APPROVED &&
        invoice.isClientComplianceStatusNotVerified;

    const isServiceDateControlEnabled = useIsFeatureEnabled(FeatureFlag.SERVICE_DATE_CONTROL);
    const {
        handleSubmit,
        trigger,
        formState: { isDirty },
    } = useFormContext<InvoiceForm>();
    const hasUnsavedChanges = isDirty || !!hasOpenEdit;
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: ['INVOICE_STATUS_TYPE', 'INVOICE_EXTERNAL_STATUS_TYPE'],
    });
    const effectiveFormat = useEffectiveDateFormat();
    const formatDate = useFormatDate();

    const {
        openPostponeModal,
        openRejectModal,
        possibleTransitions,
        isLoading: isLoadingUpdateInvoice,
    } = useInvoiceTransition(invoice, handleSubmit, templateValidation, onPostponeSuccess);
    const isInViewMode = !invoice.isEditable;
    const isSplitInvoice = invoice.isSplit ?? false;

    const [markAsSent, { isLoading: markAsSentIsLoading }] = useMarkAsSentMutation();
    const [sendToExternalApproval, { isLoading: isSendingToKsef }] = useSendToExternalApprovalMutation();
    const [
        sendToNetvisor,
        { isLoading: isSendingToNetvisor, data: netvisorPushResult, reset: resetNetvisorPushResult },
    ] = useSendToNetvisorMutation();
    const { data: isNetvisorEnabled } = useIsNetvisorIntegrationEnabledQuery();
    const [netvisorDialogOpen, setNetvisorDialogOpen] = useState(false);
    const [sendToInvoicing, { isLoading: isSendingToMaventa, reset: resetMaventaSendResult }] =
        useSendToInvoicingMutation();
    const { data: isMaventaEnabled } = useIsMaventaIntegrationEnabledQuery();
    const [maventaDialogOpen, setMaventaDialogOpen] = useState(false);
    const maventaReasonId = useId();
    const storeDispatch = useDispatch();
    const [skipKsef, { isLoading: isSkippingKsef }] = useSkipKsefMutation();
    const canBypassKsef = useHasPermissions(['UPDATE.INVOICES.BY_PASS_KSEF']);
    const [resetKsef, { isLoading: isResettingKsef }] = useResetKsefMutation();
    const canResetKsef = useHasPermissions(['UPDATE.INVOICES.RESET_EXTERNAL_EVENTS']);
    const { validateAndProceed } = useInvoiceSendValidation([invoice]);
    const canGoBackToFinalised = useHasPermissions(SENT_TO_FINALIZED_PERMISSION);

    useEffect(() => {
        handleStatusLoadingState(isLoadingUpdateInvoice);
    }, [handleStatusLoadingState, isLoadingUpdateInvoice]);

    // `reset` changes identity once a send starts, so as a dependency it would wipe the fresh result.
    const resetSendResults = useEffectEvent(() => {
        resetNetvisorPushResult();
        resetMaventaSendResult();
    });
    useEffect(() => {
        resetSendResults();
    }, [invoice.id]);

    const handleNetvisorSend = async (attachSpec: boolean) => {
        setNetvisorDialogOpen(false);
        resetNetvisorPushResult();
        try {
            await sendToNetvisor({ id: invoice.id, attachSpec }).unwrap();
        } catch (error: unknown) {
            errorAlert(readApiErrorBody(error)?.message || t('invoices.netvisor.send-error'));
        }
    };

    const handleMaventaSend = async (attachSpec: boolean) => {
        setMaventaDialogOpen(false);
        resetMaventaSendResult();
        try {
            await sendToInvoicing({ id: invoice.id, attachSpec }).unwrap();
        } catch (error: unknown) {
            const body = readApiErrorBody(error);
            errorAlert(body?.message || body?.detail || t('invoices.maventa.send-error'));
        }
    };

    const isSentToMaventa = invoice.isPresentInInvoicingIntegration === true;

    // Maventa reports a failed delivery later, through the status job; refetching when the user comes back to the
    // window lets the button free itself without polling or a page reload. The status job runs every 15 minutes, so
    // one refetch per half minute is plenty, however often the window is focused. The throttle is per invoice, so
    // moving to another invoice never swallows that invoice's first refetch.
    const lastFocusRefetch = useRef({ invoiceId: 0, at: 0 });
    useEffect(() => {
        if (!(isMaventaEnabled && isSentToMaventa && invoice.invoiceStatusTypeCode === InvoiceStatusType.FINALIZED)) {
            return undefined;
        }
        const refetchInvoice = () => {
            const now = Date.now();
            const last = lastFocusRefetch.current;
            if (last.invoiceId === invoice.id && now - last.at < FOCUS_REFETCH_INTERVAL_MS) return;
            lastFocusRefetch.current = { invoiceId: invoice.id, at: now };
            storeDispatch(APIInvoicesSlice.util.invalidateTags([{ type: 'Invoices', id: invoice.id }]));
        };
        window.addEventListener('focus', refetchInvoice);
        return () => window.removeEventListener('focus', refetchInvoice);
    }, [isMaventaEnabled, isSentToMaventa, invoice.id, invoice.invoiceStatusTypeCode, storeDispatch]);

    const steps = useMemo<StageStep[]>(() => {
        return getInvoiceStageStatuses(invoiceTypeCode, invoiceStatusTypeCode).map((status) => {
            const lastStatusTransition = findLatestNonRejectedTransition(invoiceTransitions, status);
            // The date and the person the stage names: its latest transition's (Paid's payment date), else Draft's creation.
            const when =
                lastStatusTransition !== undefined
                    ? {
                          employee: lastStatusTransition.changedByEmployee,
                          date:
                              status === InvoiceStatusType.PAID && paidOnDate
                                  ? format(new Date(paidOnDate), effectiveFormat)
                                  : format(new Date(lastStatusTransition.createdAt), effectiveFormat),
                      }
                    : status === InvoiceStatusType.DRAFT
                      ? {
                            employee: invoice.createdByEmployee,
                            date: format(new Date(invoice.createdAt), effectiveFormat),
                        }
                      : undefined;
            const maybeTimestamp = when
                ? { content: <StepDate changedByEmployee={when.employee} date={when.date} /> }
                : {};

            let label = getClassifierText(classifiers['INVOICE_STATUS_TYPE'], status);
            let externalEvents: InvoiceExternalEventDto[] | undefined;
            let subSteps: { label: string; date?: string }[] | undefined;
            if (status === InvoiceStatusType.FINALIZED) {
                if (isExternalApprovalEnabled && externalStatus && externalStatus !== ExternalStatusType.IGNORE) {
                    const externalStatusText = getClassifierText(
                        classifiers['INVOICE_EXTERNAL_STATUS_TYPE'],
                        externalStatus
                    );
                    if (externalStatusText) {
                        label = `${label} (${externalStatusText})`;
                    }
                    externalEvents = invoice.invoiceExternalEvents;
                }
                if (isNetvisorEnabled && (invoice.isPresentInAccountingIntegration || netvisorPushResult)) {
                    subSteps = [{ label: t('invoices.netvisor.sent-substatus') }];
                }
                if (isMaventaEnabled && isSentToMaventa) {
                    subSteps = [...(subSteps ?? []), { label: t('invoices.maventa.sent-substatus') }];
                }
            }

            return {
                status,
                label,
                ...maybeTimestamp,
                when,
                isSkipped: !wasEverInStatus(invoiceTransitions, status) && status !== InvoiceStatusType.DRAFT,
                externalEvents,
                subSteps,
            };
        });
    }, [
        classifiers,
        invoice,
        invoiceStatusTypeCode,
        invoiceTypeCode,
        invoiceTransitions,
        paidOnDate,
        isExternalApprovalEnabled,
        externalStatus,
        effectiveFormat,
        isNetvisorEnabled,
        netvisorPushResult,
        isMaventaEnabled,
        isSentToMaventa,
    ]);

    const hasNetvisorMapping = invoice.isPresentInAccountingIntegration === true;

    const activeStep = steps.findIndex((step) => step.status === invoiceStatusTypeCode);

    const responsibleOrSupervisingText = getResponsibleOrSupervisingText(invoiceStatusTypeCode as InvoiceStatusType);
    const responsibleEmployeesByState = getResponsibleEmployeesByState(invoice);
    const { isRequestedToPostpone } = useInvoiceFlow(invoice);
    const validateServiceDate = useCallback((): boolean => {
        if (isServiceDateControlEnabled && !invoice.serviceDate) {
            trigger('serviceDate');
            warningAlert(t('invoices.general-tab.invoice-details.service-date-required'));
            return false;
        }
        return true;
    }, [isServiceDateControlEnabled, invoice.serviceDate, trigger]);

    const hasMatterCapError =
        invoice.invoiceTypeCode === InvoiceType.DEBIT &&
        invoice.invoiceStatusTypeCode !== InvoiceStatusType.DRAFT &&
        !isSplitInvoice &&
        invoice.invoiceMatters?.some((item) => item.hasMatterCapError);

    const isExternalApprovalActive =
        isExternalApprovalEnabled && externalStatus !== ExternalStatusType.IGNORE && externalStatus !== undefined;

    const isExternalRejectBlocked =
        isExternalApprovalActive &&
        externalStatus !== ExternalStatusType.REJECTED &&
        externalStatus !== ExternalStatusType.NOT_SENT;
    const canSendKsefApprovedInvoice = useHasPermissions(['UPDATE.INVOICES.SEND_KSEF_APPROVED_INVOICE']);

    const isKsefApprovedOrSkipped =
        isExternalApprovalActive &&
        invoice.invoiceStatusTypeCode === InvoiceStatusType.FINALIZED &&
        (externalStatus === ExternalStatusType.APPROVED || externalStatus === ExternalStatusType.SKIPPED) &&
        canSendKsefApprovedInvoice;
    const isFinalizedInvoice = invoice.invoiceStatusTypeCode === InvoiceStatusType.FINALIZED;
    const canShowSendToNetvisor = Boolean(isNetvisorEnabled) && isFinalizedInvoice;
    // Unlike the Netvisor button, this one is for editors only: the backend grants the Maventa send with the edit
    // rule alone, with no approved-client exception, so a viewer has nothing to click.
    const canShowSendToMaventa = Boolean(isMaventaEnabled) && isFinalizedInvoice && !isInViewMode;
    const canShowApprovedClientSendActionsInViewMode =
        isInViewMode &&
        Boolean(isNetvisorEnabled) &&
        isFinalizedInvoice &&
        (hasNetvisorMapping || Boolean(netvisorPushResult)) &&
        possibleTransitions.main?.to === InvoiceStatusType.SENT;
    const isInvoiceInApprovedState = invoice?.invoiceStatusTypeCode === InvoiceStatusType.APPROVED;
    const canRejectApprovedInvoice = useHasPermissions(['UPDATE.INVOICES.REJECT_APPROVED_INVOICE']);
    const canShowRejectApprovedActionInViewMode =
        isInViewMode &&
        isInvoiceInApprovedState &&
        canRejectApprovedInvoice &&
        possibleTransitions.secondary?.to === InvoiceStatusType.REVIEWED;
    const canShowSendActions = !isInViewMode || isKsefApprovedOrSkipped || canShowApprovedClientSendActionsInViewMode;
    const isBlockedByExternalApprove =
        isExternalApprovalActive &&
        isFinalizedInvoice &&
        externalStatus !== ExternalStatusType.APPROVED &&
        !isKsefApprovedOrSkipped;

    // The backend refuses to move a copy on while its group's shares do not add up to 100 %.
    const sharedPaymentIncompleteReason =
        isFinalizedInvoice && sharedPaymentGroup?.isComplete === false
            ? t(
                  sharedPaymentGroup.isLocked
                      ? 'invoices.shared-payment.send-blocked-incomplete-locked'
                      : 'invoices.shared-payment.send-blocked-incomplete',
                  { total: formatShareRate(sharedPaymentGroup.shareTotal) }
              )
            : '';
    const rejectDisabledReason =
        isSharedPaymentCopy && isSplitInvoice ? t('invoices.shared-payment.reject-blocked') : '';

    // Disable going forward if signed by is not set or there are unsaved changes, to prevent confusion
    const disableNextStep =
        isLoading || buttonsDisabled || isLoadingUpdateInvoice || !invoice.signedByEmployee || hasUnsavedChanges;

    const isCreditedInvoice = invoice?.invoiceStatusTypeCode === InvoiceStatusType.CREDITED;
    const isInvoiceDraft = invoice?.invoiceStatusTypeCode === InvoiceStatusType.DRAFT;
    const isInvoiceReviewed = invoice?.invoiceStatusTypeCode === InvoiceStatusType.REVIEWED;

    const isAuthorizedToTransitionDraftToReviewed = useHasPermissions(['UPDATE.INVOICES.TRANSITION.DRAFT_REVIEWED']);
    const isResponsibleEmployeeForTransition = getAllResponsibleEmployees(invoice).some(
        (employee) => employee.id === userId
    );
    const isAuthorizedToTransitionPrepaymentInvoice = useHasPermissions([
        'UPDATE.INVOICES.TYPE_PREPAYMENT_WITH_MATTER_ACCESS',
    ]);
    const canTransitionFromDraftToReviewed =
        (isPrepayment && isAuthorizedToTransitionPrepaymentInvoice) ||
        isAuthorizedToTransitionDraftToReviewed ||
        isResponsibleEmployeeForTransition;
    const isReviewTransition = possibleTransitions.main?.to === 'REVIEWED';
    const reviewBlockedByEmptyInvoice = isReviewTransition && isInvoiceEmpty;
    const reviewBlockedByLoadingContent = isReviewTransition && !isInvoiceContentLoaded;
    const reviewEmptyInvoiceText = t('invoices.tooltips.review-empty-invoice');

    const mainButtonDisabledReason = useMemo(() => {
        if (!invoice.signedByEmployee) return t('invoices.tooltips.signed-by-missing');
        if (reviewBlockedByEmptyInvoice) return reviewEmptyInvoiceText;
        if (hasMatterCapError) return t('invoices.tooltips.matter-cap-error');
        if (possibleTransitions.main?.to === 'FINALIZED' && isFinalizationDisabled)
            return t('invoices.actions.finalization-not-allowed');
        if (possibleTransitions.main?.to === 'PAID' && invoice.hasCreditInvoice)
            return t('invoices.tooltips.paid-blocked-credit-invoice');
        if (possibleTransitions.main?.to === 'SENT' && isBlockedByExternalApprove)
            return t('invoices.tooltips.send-blocked-external-approval');
        if (sharedPaymentIncompleteReason) return sharedPaymentIncompleteReason;
        return '';
    }, [
        sharedPaymentIncompleteReason,
        invoice.signedByEmployee,
        invoice.hasCreditInvoice,
        reviewBlockedByEmptyInvoice,
        reviewEmptyInvoiceText,
        hasMatterCapError,
        possibleTransitions,
        isFinalizationDisabled,
        isBlockedByExternalApprove,
    ]);

    const sendWithoutEmailDisabledReason = useMemo(() => {
        if (!invoice.signedByEmployee) return t('invoices.tooltips.signed-by-missing');
        if (isBlockedByExternalApprove) return t('invoices.tooltips.send-blocked-external-approval');
        return sharedPaymentIncompleteReason;
    }, [invoice.signedByEmployee, isBlockedByExternalApprove, sharedPaymentIncompleteReason]);

    const payersSplitByFinalising = payersSplitOnFinalise(invoice, isSharedPaymentEnabled);
    // A confirmation's title names the invoice; a draft may have no number yet.
    const confirmedInvoice = invoice.invoiceNumber || 'none';
    const openFinalisingConfirmation = (onConfirm: () => void) => {
        const finalisingMessage = (
            <FormattedMessage
                id="invoices.shared-payment.finalise-confirmation"
                values={{
                    count: payersSplitByFinalising.length,
                    names: payersSplitByFinalising.map((payer) => payer.party.name).join(', '),
                }}
            />
        );
        openModal(
            <ConfirmationModal
                onCancel={closeModal}
                onConfirm={() => {
                    closeModal();
                    onConfirm();
                }}
                content={<p className="text-body1">{finalisingMessage}</p>}
                alert={{
                    titleId: 'invoices.confirm.finalise-split-title',
                    titleValues: { count: payersSplitByFinalising.length },
                    description: finalisingMessage,
                    confirmId: 'invoices.actions.to-FINALIZED',
                }}
            />,
            closeModal
        );
    };

    const showSendToKsef =
        isExternalApprovalEnabled &&
        !isInViewMode &&
        isFinalizedInvoice &&
        (externalStatus === ExternalStatusType.NOT_SENT || externalStatus === ExternalStatusType.REJECTED);
    const showSkipKsef =
        isExternalApprovalEnabled &&
        !isInViewMode &&
        canBypassKsef &&
        isFinalizedInvoice &&
        externalStatus === ExternalStatusType.NOT_SENT;
    const showResetKsef =
        isExternalApprovalEnabled &&
        canResetKsef &&
        isFinalizedInvoice &&
        externalStatus !== undefined &&
        externalStatus !== ExternalStatusType.NOT_SENT &&
        externalStatus !== ExternalStatusType.IGNORE;
    const showMain =
        !!possibleTransitions.main && (isReviewTransition ? canTransitionFromDraftToReviewed : canShowSendActions);
    const showSendWithoutEmail = canShowSendActions && possibleTransitions.main?.to === 'SENT';
    const showSecondary =
        (!isInViewMode || isKsefApprovedOrSkipped || canShowRejectApprovedActionInViewMode) &&
        !!possibleTransitions.secondary;
    const showPostpone = canShowSendActions && !isInViewMode && isDebit && isInvoiceReviewed && !isRequestedToPostpone;

    const isMainDisabled =
        disableNextStep ||
        !!hasMatterCapError ||
        reviewBlockedByLoadingContent ||
        (possibleTransitions.main?.to === 'PAID' && invoice.hasCreditInvoice) ||
        (possibleTransitions.main?.to === 'FINALIZED' && isFinalizationDisabled) ||
        (possibleTransitions.main?.to === 'SENT' && isBlockedByExternalApprove) ||
        !!sharedPaymentIncompleteReason;
    const isSendWithoutEmailDisabled =
        disableNextStep || markAsSentIsLoading || isBlockedByExternalApprove || !!sharedPaymentIncompleteReason;
    const isSecondaryDisabled = disableNextStep || isSplitInvoice || isExternalRejectBlocked;
    const secondaryLabelId =
        possibleTransitions.secondary?.to === InvoiceStatusType.POSTPONED
            ? 'invoices.actions.request-postpone'
            : possibleTransitions.main?.to === InvoiceStatusType.POSTPONED
              ? 'invoices.actions.disagree'
              : 'invoices.actions.reject';
    const backToFinalisedReason =
        rejectDisabledReason ||
        (isPartiallyCredited
            ? t('invoices.tooltips.correction-invoice-created')
            : invoice.hasDebitInvoicedTimecardsOrExpenses
              ? t('invoices.tooltips.debit-invoice-already-created')
              : '');

    const handleMainTransition = async () => {
        if (reviewBlockedByEmptyInvoice) {
            warningAlert(reviewEmptyInvoiceText);
            return;
        }
        if (!validateServiceDate()) {
            return;
        }
        if (isMultiApprovalFlow && !isApproved && !isRequestedToPostpone) {
            openModal(
                <ConfirmationModal
                    onCancel={closeModal}
                    onConfirm={closeModal}
                    content={
                        <p className="text-body1">
                            <FormattedMessage
                                id={`invoices.joint-invoice-${isInvoiceDraft ? 'review' : 'approve'}-button-prompt-message`}
                            />
                        </p>
                    }
                    confirmId="ok"
                />
            );
        } else if (possibleTransitions.main?.to === 'SENT') {
            const isTemplateChosen = await templateValidation();
            if (!isTemplateChosen) return;
            // Validate invoice dates → show warning modal if needed
            validateAndProceed(() => {
                possibleTransitions.main?.handleTransition();
            });
        } else if (possibleTransitions.main?.to === InvoiceStatusType.FINALIZED && payersSplitByFinalising.length > 0) {
            openFinalisingConfirmation(() => possibleTransitions.main?.handleTransition());
        } else {
            possibleTransitions.main?.handleTransition();
        }
    };

    const handleSendWithoutEmail = async () => {
        const isTemplateChosen = await templateValidation();
        if (isTemplateChosen) {
            validateAndProceed(() => {
                openModal(
                    <ConfirmationModal
                        onCancel={closeModal}
                        onConfirm={() => {
                            void (async () => {
                                await markAsSent(invoice.id);
                                closeModal();
                            })();
                        }}
                        content={<FormattedMessage id="invoices.send-wo-email.confirmation" />}
                        alert={{
                            titleId: 'invoices.confirm.send-wo-email-title',
                            titleValues: { invoice: confirmedInvoice },
                            description: <FormattedMessage id="invoices.confirm.send-wo-email-text" />,
                            confirmId: 'invoices.confirm.mark-as-sent',
                        }}
                    />,
                    closeModal
                );
            });
        }
    };

    const handleSecondaryTransition = () => {
        if (
            isMultiApprovalFlow &&
            isInvoiceReviewed &&
            possibleTransitions.secondary?.to === InvoiceStatusType.DRAFT &&
            !isRequestedToPostpone
        ) {
            openModal(
                <ConfirmationModal
                    onCancel={closeModal}
                    onConfirm={closeModal}
                    content={
                        <p className="text-body1">
                            <FormattedMessage id="invoices.joint-invoice-reject-button-prompt-message" />
                        </p>
                    }
                    confirmId="ok"
                />
            );
        } else {
            possibleTransitions.secondary?.handleTransition();
        }
    };

    const handleResetKsef = () => {
        openModal(
            <ConfirmationModal
                onCancel={closeModal}
                onConfirm={() => {
                    void (async () => {
                        try {
                            await resetKsef({ id: invoice.id }).unwrap();
                        } catch (error: unknown) {
                            const message =
                                error && typeof error === 'object' && 'data' in error
                                    ? (error as { data?: { message?: string } }).data?.message
                                    : undefined;
                            errorAlert(message || t('invoices.reset-ksef.error'));
                        } finally {
                            closeModal();
                        }
                    })();
                }}
                content={<FormattedMessage id="invoices.reset-ksef.confirmation" />}
                alert={{
                    titleId: 'invoices.confirm.reset-ksef-title',
                    titleValues: { invoice: confirmedInvoice },
                    description: <FormattedMessage id="invoices.confirm.reset-ksef-text" />,
                    confirmId: 'invoices.actions.reset-ksef',
                    tone: 'danger',
                }}
            />,
            closeModal
        );
    };

    const integrationDialogs = (
        <>
            <SendToIntegrationDialog
                open={maventaDialogOpen}
                onClose={() => setMaventaDialogOpen(false)}
                onConfirm={(attachSpec) => {
                    void handleMaventaSend(attachSpec);
                }}
                invoiceNumber={invoice.invoiceNumber}
                isSpecAvailable={invoice.isSpecAvailable}
                defaultAttachSpec={isSpecAttachedByDefault(invoice)}
                isSending={isSendingToMaventa}
                messageIds={MAVENTA_DIALOG_MESSAGE_IDS}
                warning={
                    invoice.isInvoicingReferenceMissing
                        ? t('invoices.maventa.confirm-dialog.reference-missing')
                        : undefined
                }
            />
            <SendToIntegrationDialog
                open={netvisorDialogOpen}
                onClose={() => setNetvisorDialogOpen(false)}
                onConfirm={(attachSpec) => {
                    void handleNetvisorSend(attachSpec);
                }}
                invoiceNumber={invoice.invoiceNumber}
                isSpecAvailable={invoice.isSpecAvailable}
                isSending={isSendingToNetvisor}
                messageIds={NETVISOR_DIALOG_MESSAGE_IDS}
            />
        </>
    );

    const netvisorWarnings =
        netvisorPushResult && netvisorPushResult.warnings.length > 0 ? (
            <Alert className="bg-paper text-fg mt-2 flex items-center gap-2 rounded-md border-transparent py-2 pr-4 pl-3 text-sm leading-[1.125rem] font-semibold">
                <span className="flex w-6 shrink-0 justify-center [&>svg]:size-4">
                    <WarningAmberOutlined className="text-warning" />
                </span>
                <div className="min-w-0">
                    <strong>
                        <FormattedMessage id="invoices.netvisor.warnings.title" />
                    </strong>
                    <ul className="mt-1 list-disc pl-5">
                        {netvisorPushResult.warnings.map((w) => (
                            // TODO: stable key — warnings are plain strings; using value as key is acceptable when unique
                            <li key={w}>{w}</li>
                        ))}
                    </ul>
                </div>
            </Alert>
        ) : null;

    const footerJsx =
        invoice.invoiceStatusTypeCode === InvoiceStatusType.SENT ? (
            <ReasonTooltip reason={backToFinalisedReason}>
                <div className="inline-flex">
                    <PermissionButton
                        key="sent-to-finalized"
                        permissions={SENT_TO_FINALIZED_PERMISSION}
                        variant="outlined"
                        size="small"
                        onClick={() => openRejectModal(InvoiceStatusType.FINALIZED)}
                        disabled={
                            !!rejectDisabledReason || isPartiallyCredited || invoice.hasDebitInvoicedTimecardsOrExpenses
                        }
                    >
                        <FormattedMessage id="invoices.actions.back-to-finalised" />
                    </PermissionButton>
                </div>
            </ReasonTooltip>
        ) : (
            <div className="mt-6 flex flex-col gap-4">
                {canShowSendActions &&
                    !isCreditedInvoice &&
                    (possibleTransitions.main || possibleTransitions.secondary) && (
                        <h6 className="text-h6 font-semibold">
                            <FormattedMessage id="invoices.pending-steps" />
                        </h6>
                    )}
                <div className="flex flex-col gap-2">
                    {!!responsibleEmployeesByState.length && responsibleOrSupervisingText && (
                        <div>
                            <p className="text-body1 text-fg-secondary">
                                <FormattedMessage id={responsibleOrSupervisingText} />
                            </p>
                            {responsibleEmployeesByState.map((employee, id) => (
                                <p key={`emp#${id}`} className="text-body1 text-fg">
                                    <EmployeeName entity={employee} />
                                </p>
                            ))}
                        </div>
                    )}
                    {isRequestedToPostpone && invoice.invoiceTransitions.length && (
                        <div className="w-[216px]">
                            <InvoiceTransitionAlertMessage
                                messageId="invoices.alerts.requests-postpone-name"
                                lastTransition={invoice.invoiceTransitions[0]}
                            />
                        </div>
                    )}
                    {isFinalizationDisabled && (
                        <p className="text-subtitle2 border-error bg-error-light rounded-sm border p-2">
                            <FormattedMessage id="invoices.actions.finalization-not-allowed" />
                        </p>
                    )}
                    {showSendToKsef && (
                        <Button
                            key={`${invoiceStatusTypeCode}-send-to-ksef`}
                            disabled={disableNextStep || isSendingToKsef}
                            {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                            onClick={() => {
                                void sendToExternalApproval({ id: invoice.id });
                            }}
                        >
                            <FormattedMessage id="invoices.actions.send-to-ksef" />
                        </Button>
                    )}
                    {canShowSendToNetvisor && (
                        <Button
                            key={`${invoiceStatusTypeCode}-send-to-netvisor`}
                            disabled={disableNextStep || isSendingToNetvisor}
                            {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                            onClick={() => setNetvisorDialogOpen(true)}
                        >
                            <FormattedMessage
                                id={
                                    hasNetvisorMapping || Boolean(netvisorPushResult)
                                        ? 'invoices.actions.resend-to-netvisor'
                                        : 'invoices.actions.send-to-netvisor'
                                }
                            />
                        </Button>
                    )}
                    {netvisorWarnings}
                    {canShowSendToMaventa && (
                        <>
                            <ReasonTooltip reason={isSentToMaventa ? t('invoices.maventa.already-sent-reason') : ''}>
                                <Button
                                    key={`${invoiceStatusTypeCode}-send-to-maventa`}
                                    disabled={disableNextStep || isSendingToMaventa || isSentToMaventa}
                                    focusableWhenDisabled
                                    aria-describedby={isSentToMaventa ? maventaReasonId : undefined}
                                    {...buttonStyle({ variant: 'contained', color: 'secondary' }, OFF_SECONDARY_CLASS)}
                                    onClick={() => setMaventaDialogOpen(true)}
                                >
                                    <FormattedMessage id="invoices.actions.send-to-maventa" />
                                </Button>
                            </ReasonTooltip>
                            {isSentToMaventa && (
                                <span id={maventaReasonId} hidden>
                                    <FormattedMessage id="invoices.maventa.already-sent-reason" />
                                </span>
                            )}
                        </>
                    )}
                    {integrationDialogs}
                    {showSkipKsef && (
                        <Button
                            key={`${invoiceStatusTypeCode}-skip-ksef`}
                            disabled={disableNextStep || isSkippingKsef}
                            {...buttonStyle({ variant: 'outlined', color: 'secondary' })}
                            onClick={() => {
                                void skipKsef({ id: invoice.id });
                            }}
                        >
                            <FormattedMessage id="invoices.actions.skip-ksef" />
                        </Button>
                    )}
                    {showResetKsef && (
                        <Button
                            key={`${invoiceStatusTypeCode}-reset-ksef`}
                            disabled={isLoading || buttonsDisabled || isResettingKsef}
                            {...buttonStyle({ variant: 'outlined', color: 'secondary' })}
                            onClick={handleResetKsef}
                        >
                            <FormattedMessage id="invoices.actions.reset-ksef" />
                        </Button>
                    )}
                    {showMain && possibleTransitions.main && (
                        <ReasonTooltip reason={mainButtonDisabledReason}>
                            <div className="flex">
                                <Button
                                    key={`${invoiceStatusTypeCode}-approve`}
                                    disabled={isMainDisabled}
                                    {...buttonStyle({ variant: 'contained', color: 'secondary' }, 'grow')}
                                    onClick={() => {
                                        void handleMainTransition();
                                    }}
                                >
                                    <FormattedMessage id={`invoices.actions.to-${possibleTransitions.main.to}`} />
                                </Button>
                            </div>
                        </ReasonTooltip>
                    )}
                    {showSendWithoutEmail && (
                        <ReasonTooltip reason={sendWithoutEmailDisabledReason}>
                            <div className="flex">
                                <Button
                                    key={`${invoiceStatusTypeCode}-send-wo-email`}
                                    disabled={isSendWithoutEmailDisabled}
                                    {...buttonStyle({ variant: 'outlined' }, 'grow')}
                                    onClick={() => {
                                        void handleSendWithoutEmail();
                                    }}
                                >
                                    <FormattedMessage id={'invoices.actions.to-SENT-wo-email'} />
                                </Button>
                            </div>
                        </ReasonTooltip>
                    )}
                    {showSecondary && (
                        <ReasonTooltip reason={rejectDisabledReason}>
                            <div className="flex">
                                <Button
                                    key={`${invoiceStatusTypeCode}-reject`}
                                    disabled={isSecondaryDisabled}
                                    {...buttonStyle({ variant: 'outlined' }, 'grow')}
                                    onClick={handleSecondaryTransition}
                                >
                                    <FormattedMessage id={secondaryLabelId} />
                                </Button>
                            </div>
                        </ReasonTooltip>
                    )}
                    {canShowSendActions && (
                        <>
                            {showPostpone && (
                                <Button
                                    key={`${invoiceStatusTypeCode}-postpone`}
                                    disabled={disableNextStep}
                                    {...buttonStyle({ variant: 'outlined' })}
                                    onClick={openPostponeModal}
                                >
                                    <FormattedMessage id="invoices.actions.postpone" />
                                </Button>
                            )}
                            {!invoice.signedByEmployee || hasUnsavedChanges ? (
                                <div className="max-w-[200px]">
                                    {!invoice.signedByEmployee ? (
                                        <p className="text-body1 text-error">
                                            <FormattedMessage id="invoices.actions.signed-by-required" />
                                        </p>
                                    ) : hasUnsavedChanges ? (
                                        <p className="text-body1 text-fg-secondary">
                                            <FormattedMessage id="invoices.actions.save-or-discard" />
                                        </p>
                                    ) : null}
                                </div>
                            ) : null}
                        </>
                    )}
                </div>
            </div>
        );

    if (isLoadingClassifiers) {
        return null;
    }

    if (renderBand) {
        const nextStepReason = !invoice.signedByEmployee
            ? t('invoices.actions.signed-by-required')
            : hasUnsavedChanges
              ? t('invoices.actions.save-or-discard')
              : '';
        // A joint invoice is reviewed and approved case by case, on the cases' cards: the band's buttons wait and say
        // so, where classic explains it in a dialog on the press.
        const jointReason =
            isMultiApprovalFlow && !isApproved && !isRequestedToPostpone
                ? t(`invoices.joint-invoice-${isInvoiceDraft ? 'review' : 'approve'}-button-prompt-message`)
                : '';
        const isJointReject =
            isMultiApprovalFlow &&
            isInvoiceReviewed &&
            possibleTransitions.secondary?.to === InvoiceStatusType.DRAFT &&
            !isRequestedToPostpone;
        const actions: (InvoiceWorkflowAction | false)[] =
            invoiceStatusTypeCode === InvoiceStatusType.SENT
                ? [
                      canGoBackToFinalised && {
                          key: 'back-to-finalised',
                          labelId: 'invoices.actions.back-to-finalised',
                          look: 'outlined',
                          onClick: () => openRejectModal(InvoiceStatusType.FINALIZED),
                          disabled:
                              !!rejectDisabledReason ||
                              isPartiallyCredited ||
                              !!invoice.hasDebitInvoicedTimecardsOrExpenses,
                          reason: backToFinalisedReason,
                      },
                  ]
                : [
                      showPostpone && {
                          key: 'postpone',
                          labelId: 'invoices.actions.postpone',
                          look: 'plain',
                          onClick: openPostponeModal,
                          disabled: disableNextStep,
                          reason: nextStepReason,
                      },
                      showSecondary && {
                          key: 'secondary',
                          labelId: secondaryLabelId,
                          look: isFinalizedInvoice ? 'plain' : 'outlined',
                          onClick: handleSecondaryTransition,
                          disabled: isSecondaryDisabled || isJointReject,
                          // Beside a main button that waits for the cases too, one line says it for both.
                          reason:
                              nextStepReason ||
                              rejectDisabledReason ||
                              (isJointReject
                                  ? jointReason || t('invoices.joint-invoice-reject-button-prompt-message')
                                  : ''),
                      },
                      showSendWithoutEmail && {
                          key: 'send-wo-email',
                          labelId: 'invoices.actions.to-SENT-wo-email',
                          look: 'outlined',
                          onClick: () => void handleSendWithoutEmail(),
                          disabled: isSendWithoutEmailDisabled,
                          reason: nextStepReason || sendWithoutEmailDisabledReason,
                      },
                      showSendToKsef && {
                          key: 'send-to-ksef',
                          labelId: 'invoices.actions.send-to-ksef',
                          look: 'outlined',
                          onClick: () => void sendToExternalApproval({ id: invoice.id }),
                          disabled: disableNextStep || isSendingToKsef,
                          reason: nextStepReason,
                      },
                      showSkipKsef && {
                          key: 'skip-ksef',
                          labelId: 'invoices.actions.skip-ksef',
                          look: 'outlined',
                          onClick: () => void skipKsef({ id: invoice.id }),
                          disabled: disableNextStep || isSkippingKsef,
                          reason: nextStepReason,
                      },
                      showResetKsef && {
                          key: 'reset-ksef',
                          labelId: 'invoices.actions.reset-ksef',
                          look: 'outlined',
                          onClick: handleResetKsef,
                          disabled: isLoading || buttonsDisabled || isResettingKsef,
                      },
                      canShowSendToNetvisor && {
                          key: 'send-to-netvisor',
                          labelId:
                              hasNetvisorMapping || Boolean(netvisorPushResult)
                                  ? 'invoices.actions.resend-to-netvisor'
                                  : 'invoices.actions.send-to-netvisor',
                          look: 'outlined',
                          onClick: () => setNetvisorDialogOpen(true),
                          disabled: disableNextStep || isSendingToNetvisor,
                          reason: nextStepReason,
                      },
                      canShowSendToMaventa && {
                          key: 'send-to-maventa',
                          labelId: 'invoices.actions.send-to-maventa',
                          look: 'outlined',
                          onClick: () => setMaventaDialogOpen(true),
                          disabled: disableNextStep || isSendingToMaventa || isSentToMaventa,
                          reason: nextStepReason || (isSentToMaventa ? t('invoices.maventa.already-sent-reason') : ''),
                      },
                      showMain &&
                          !!possibleTransitions.main && {
                              key: 'main',
                              labelId: `invoices.actions.to-${possibleTransitions.main.to}`,
                              look: 'primary',
                              onClick: () => void handleMainTransition(),
                              // An empty invoice's Review waits with its reason, where classic warns on the press.
                              disabled: isMainDisabled || reviewBlockedByEmptyInvoice || !!jointReason,
                              reason: nextStepReason || jointReason || mainButtonDisabledReason,
                          },
                  ];
        const statuses = steps.map((step) => step.status);
        return renderBand({
            stages: steps.map((step, index) => ({
                status: step.status,
                label: step.label,
                state: getInvoiceStageState(statuses, index, invoiceStatusTypeCode, invoiceTransitions),
                date: step.when?.date,
                by: step.when?.employee?.name,
                details: [
                    // An event's reason is a line of its own under it, where classic has it in a tooltip.
                    ...(step.externalEvents ?? []).flatMap((event) => [
                        [
                            `${t(event.directionTypeCode === 'INBOUND' ? 'invoices.ksef.event.inbound' : 'invoices.ksef.event.outbound')}${event.externalInvoiceState ? ` (${event.externalInvoiceState})` : ''}`,
                            formatDate(event.eventTimestamp, true) ?? '',
                        ]
                            .filter(Boolean)
                            .join(' · '),
                        ...(event.externalInvoiceStateReason ? [event.externalInvoiceStateReason] : []),
                    ]),
                    ...(step.subSteps ?? []).map((subStep) => subStep.label),
                ],
            })),
            // A credit or correction draft goes straight to Finalise: its next stage names no one.
            pending:
                responsibleOrSupervisingText &&
                responsibleEmployeesByState.length > 0 &&
                !((isCredit || isCorrection) && isInvoiceDraft)
                    ? { roleId: responsibleOrSupervisingText, employees: responsibleEmployeesByState }
                    : undefined,
            actions: actions.filter((action): action is InvoiceWorkflowAction => !!action),
            extras: (
                <>
                    {/* For every viewer, as classic's: Finalise's reason line reaches only those who see Finalise. */}
                    {isFinalizationDisabled ? (
                        <Alert className="border-destructive bg-danger-tint text-danger-ink mt-4">
                            <CircleAlert aria-hidden />
                            <AlertTitle>
                                <FormattedMessage id="invoices.actions.finalization-not-allowed" />
                            </AlertTitle>
                        </Alert>
                    ) : null}
                    {netvisorWarnings}
                    {integrationDialogs}
                </>
            ),
        });
    }

    return (
        <Workflow
            steps={steps}
            activeStep={activeStep}
            footer={footerJsx}
            isMultiApproval={isMultiApprovalFlow}
            isExternalApproval={isExternalApprovalEnabled}
            invoiceMatters={invoice.invoiceMatters || []}
        />
    );
};

export default InvoiceWorkflow;
